import React, { useState, createContext, useEffect, useContext } from 'react';
import jwtDecode from 'jwt-decode';
import { gql, useLazyQuery } from '@apollo/client';
import { client } from '../services/Graph';
import { AuthContext_AccountData_account } from '../types/graph/AuthContext_AccountData';
import getModalContext from './getModalContext';
export interface IAuthContext {
  isAuthenticated: boolean;
  setIsAuthenticated: (value: boolean) => void;
  login: (authData: any) => boolean;
  logout: () => void;
  account: AuthContext_AccountData_account | null;
  getTokenMeta: () => TokenMeta | null;
  getAccountId: () => string;
}

type TokenMeta = {
  uid: string;
  aid: string;
  rurl: string;
  arol: string[];
};

type AuthData = {
  accessToken: string;
  tokenType: string;
  expiresIn: number;
};

const AuthContext = createContext<IAuthContext>({
  isAuthenticated: true,
  setIsAuthenticated: (value) => {},
  login: (authData) => {
    return false;
  },
  logout: () => {},
  account: null,
  getTokenMeta: () => null,
  getAccountId: () => '',
});

const AuthProvider: React.FC = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [account, setAccount] =
    useState<AuthContext_AccountData_account | null>(null);
  const modals = useContext(getModalContext());

  const [getAccount, { called, loading, data }] = useLazyQuery(GET_ACCOUNT, {
    onCompleted: (data) => {
      setAccount(data.account);

      // Have user set password
      if (data.account.user.state[0].status === 'pending') {
        modals?.setModalState('passwordModal', true, {
          account: data.account,
        });
      }
    },
  });

  useEffect(() => {
    try {
      let token = localStorage.getItem('accessToken');
      setIsAuthenticated(true);
      if (token) {
        if (!account) {
          let decoded: TokenMeta = jwtDecode(token);
          getAccount({ variables: { accountId: decoded.aid } });
        }
      } else {
        logout();
      }
    } catch (error) {
      console.log(error);
    }
  }, []);

  const login = (authData: AuthData) => {
    if (!_checkTokenViability(authData.accessToken)) return false;

    let decoded: TokenMeta;
    try {
      decoded = jwtDecode(authData.accessToken);
      localStorage.setItem('accessToken', authData.accessToken);
      setIsAuthenticated(true);
      getAccount({ variables: { accountId: decoded.aid } });
    } catch (error) {
      console.log(error);
      return false;
    }
    return true;
  };

  const logout = async () => {
    setIsAuthenticated(false);
    localStorage.removeItem('accessToken');
    await client.clearStore();
  };

  const getTokenMeta = () => {
    const accessToken = localStorage.getItem('accessToken');

    if (!accessToken) throw Error('No accesstoken found in local storage');

    let decoded: TokenMeta = jwtDecode(accessToken);
    return decoded;
  };

  const getAccountId = () => {
    return getTokenMeta().aid;
  };

  const _checkTokenViability = (accessToken: string) => {
    let decoded: TokenMeta;

    try {
      decoded = jwtDecode(accessToken);

      if (!Array.isArray(decoded.arol) || !decoded.arol.includes('member')) {
        return false;
      }
    } catch (error) {
      console.log(error);
      return false;
    }
    return true;
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        setIsAuthenticated,
        account,
        login,
        logout,
        getTokenMeta,
        getAccountId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };

export const GET_ACCOUNT = gql`
  query AuthContext_AccountData($accountId: String!) {
    account(accountId: $accountId) {
      id
      roles
      scope
      user {
        id
        username
        roles
        scope
        person {
          name {
            first
            last
            full
          }
          email
          phones {
            number
            type
          }
          avatar {
            filePath
          }
          socialLinks {
            url
            type
          }
        }
        company {
          name
          title
          socialLinks {
            url
            type
          }
        }
        state {
          status
          setAt
        }
      }
      users {
        id
        username
        roles
        scope
        person {
          name {
            first
            last
            full
          }
          email
          phones {
            number
            type
          }
          avatar {
            filePath
          }
          socialLinks {
            url
            type
          }
        }
        company {
          name
          title
          socialLinks {
            url
            type
          }
        }
        state {
          status
          setAt
        }
      }
      integrations {
        facebook {
          userId
          status
          accessToken
          pages {
            id
            name
            accessToken
          }
        }
      }
    }
  }
`;
